import { useGetAllDepartmentsQuery } from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import { useGetAllOfferedApplicantsQuery } from '@/app/features/recruitment/hiring-pipeline/hiring-pipeline.api';
import { useGetAllJobPostsQuery } from '@/app/features/recruitment/job-applicants/job-applicants.api';
import CustomModal from '@/components/CustomModal';
import EmptyState from '@/components/EmptyState';
import ViewToggler from '@/components/ViewToggler';
import WithLoader from '@/components/WithLoader';
import { FILTER_KEYS } from '@/constants/keys.constants';
import { useAppFilters, useSelectionOptions } from '@/hooks';
import useModal from '@/hooks/useModal';
import {
  ChevronDownIcon,
  FileTextIcon,
  PaperclipIcon,
  SearchIcon,
  UserMultipleIcon,
} from '@/icons';
import { firstChar } from '@/utils/string.utils';
import { Flex, Input, Select, Typography } from 'antd';
import EmpCard from '../Evaluation/_components/EvAssessment/EmpCard';

const { Text } = Typography;

export default function Canceled() {
  const { page, limit, keyword, filters, handleFilter, handleSearch } =
    useAppFilters(FILTER_KEYS.HIRING_PIPELINE);

  const {
    open: reasonModal,
    handleOpen: openReason,
    handleClose: closeReason,
  } = useModal<{ applicant_name: string; reason: string | null }>();

  // ── Options ────────────────────────────────────────────────────────────────
  const [jobOptions, isJobsFetching] = useSelectionOptions(
    useGetAllJobPostsQuery,
    {
      labelKey: 'job_title',
      valueKey: 'id',
    },
  );

  const [departmentOptions, isDeptFetching] = useSelectionOptions(
    useGetAllDepartmentsQuery,
    {
      labelKey: 'name',
      valueKey: 'id',
    },
  );

  // ── Data ───────────────────────────────────────────────────────────────────
  const {
    data: canceledData,
    isLoading,
    isFetching,
  } = useGetAllOfferedApplicantsQuery({
    params: {
      page,
      limit,
      search: keyword,
      hiring_pipeline_status: 'cancel',
      job_id: filters?.job_id,
      department_id: filters?.department_id,
      offer_letter_status: filters?.offer_letter_status,
    },
  });

  const applicants = canceledData?.data?.data || [];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Flex
        align="start"
        justify="space-between"
        gap={16}
        className="flex-wrap lg:flex-nowrap"
      >
        <ViewToggler disabled="list" mode="grid" setMode={() => {}} />
        <Flex vertical gap={12} align="end">
          <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
            <Input
              allowClear
              size="large"
              onChange={handleSearch}
              placeholder="Search Applicant"
              prefix={<SearchIcon size={20} />}
            />
            <div>
              <Select
                allowClear
                showSearch
                size="large"
                placement="bottomRight"
                optionFilterProp="label"
                placeholder="Job Position"
                popupMatchSelectWidth={false}
                loading={isJobsFetching as boolean}
                onChange={(value) => handleFilter('job_id', value)}
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={jobOptions as any[]}
              />
            </div>
            <div>
              <Select
                allowClear
                showSearch
                size="large"
                placement="bottomRight"
                optionFilterProp="label"
                placeholder="Department"
                popupMatchSelectWidth={false}
                loading={isDeptFetching as boolean}
                onChange={(value) => handleFilter('department_id', value)}
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={departmentOptions as any[]}
              />
            </div>
          </Flex>
        </Flex>
      </Flex>

      {/* Cards */}
      <WithLoader isLoading={isLoading || isFetching}>
        {applicants.length === 0 ? (
          <EmptyState
            icon={<UserMultipleIcon size={40} />}
            title="No Canceled Applicants"
            description="No applicants have been canceled yet, or none match your current filters."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {applicants.map((u) => (
              <EmpCard
                key={u.offer_letter_id}
                showStatus={false}
                item={{
                  name: u.applicant_name,
                  photo: u.photo,
                  designation: u.job_title,
                  department: u.department_name,
                  assessment_status: u.offer_letter_status,
                }}
                actionRender={
                  <div className="space-y-2">
                    <button
                      onClick={() =>
                        openReason({
                          data: {
                            applicant_name: u.applicant_name,
                            reason: u.hiring_pipeline_cancel_reason,
                          },
                        })
                      }
                      className="text-theme-primary font-semibold cursor-pointer underline underline-offset-2"
                    >
                      Reason
                    </button>
                    {u.cv_link && (
                      <Flex
                        gap={4}
                        align="center"
                        justify="center"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(u.cv_link, '_blank');
                        }}
                        className="w-full p-1 bg-theme-gray-90 rounded text-theme-slate-800 text-sm cursor-pointer"
                      >
                        <PaperclipIcon size={14} className="text-[#007FC4]" />
                        {firstChar(u.cv_link.split('/').pop() ?? 'CV')}...pdf
                      </Flex>
                    )}
                  </div>
                }
              />
            ))}
          </div>
        )}
      </WithLoader>

      <CustomModal
        open={reasonModal.open}
        handleClose={closeReason}
        size={500}
        title="Cancellation Reason"
      >
        <div className="space-y-4 pt-4">
          <div className="space-y-1">
            <Text className="text-theme-slate-400 text-base font-semibold block">
              Applicant Name
            </Text>
            <Text className="text-white text-lg">
              {reasonModal.data?.applicant_name}
            </Text>
          </div>

          <div className="space-y-1">
            <Text className="text-theme-slate-400 text-base font-semibold block">
              Reason for Cancellation
            </Text>
            <div className="bg-theme-slate-900/50 p-4 rounded-xl border border-theme-gray-700 mt-2">
              <Text className="text-white text-[15px] leading-relaxed block">
                {reasonModal.data?.reason || 'No specific reason provided.'}
              </Text>
            </div>
          </div>

          <div className="flex justify-center pt-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-theme-primary/10">
              <FileTextIcon size={24} className="text-theme-primary" />
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
